import { useCallback, useMemo, useState } from 'react'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { PageContainer } from '@/components/common/PageContainer'
import { PageEmpty, PageError, PageLoading } from '@/components/common/PageStates'
import { DocumentListRow } from '@/components/rfid/DocumentListRow'
import { DocumentListToolbar } from '@/components/rfid/DocumentListToolbar'
import { RfidAppHeader } from '@/components/rfid/RfidAppHeader'
import { getMovement, type MovementId } from '@/config/movements'
import { rfidService, type RfidDocumentRow } from '@/services/rfid.service'
import { baseDocToWizardDocument } from '@/utils/transferDocument'
import { movementFlowLabel } from '@/utils/movementFlow'
import {
  applyDocumentListFilters,
  buildCustomerFilterOptions,
  buildPlantFilterOptions,
  EMPTY_DOCUMENT_LIST_FILTERS,
  type DocumentListFilterState,
} from '@/utils/documentListFilters'

const BASE_DOC_STORAGE_KEY = 'rfid-transfer-base-doc'

function baseDocAsRow(raw: Record<string, unknown>, customerName: string): RfidDocumentRow {
  const doc = baseDocToWizardDocument(raw, customerName)
  return {
    ...doc,
    CustName: customerName,
    PostingDate: String(raw.EFFECTIVE_DATE ?? raw.PostingDate ?? ''),
  }
}

export default function TransferDocListPage() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const movementParam = params.get('movement')
  const bpCode = params.get('bpCode') ?? ''
  const customerName = params.get('customerName') ?? ''
  const [filters, setFilters] = useState<DocumentListFilterState>(EMPTY_DOCUMENT_LIST_FILTERS)

  const valid = movementParam === 'transfer_in' && Boolean(bpCode)
  const movement = valid ? getMovement(movementParam as MovementId) : null

  const docsQ = useQuery({
    queryKey: ['transfer-base-docs', bpCode],
    queryFn: () => rfidService.fetchTransferBaseDocs(bpCode),
    enabled: Boolean(bpCode) && valid,
  })

  const rows = useMemo(() => {
    return (docsQ.data ?? []).map((raw) => ({
      raw,
      document: baseDocAsRow(raw, customerName),
    }))
  }, [docsQ.data, customerName])

  const documents = useMemo(() => rows.map((r) => r.document), [rows])
  const plantOptions = useMemo(() => buildPlantFilterOptions(documents), [documents])
  const customerOptions = useMemo(() => buildCustomerFilterOptions(documents), [documents])

  const filtered = useMemo(() => {
    let list = rows
    if (filters.search.trim()) {
      const q = filters.search.trim().toLowerCase()
      list = list.filter(({ document, raw }) =>
        JSON.stringify({ ...document, ...raw }).toLowerCase().includes(q)
      )
    }
    const narrowed = new Set(
      applyDocumentListFilters(
        list.map((r) => r.document),
        { ...filters, search: '' }
      )
    )
    return list.filter(({ document }) => narrowed.has(document))
  }, [rows, filters])

  const patchFilters = useCallback((patch: Partial<DocumentListFilterState>) => {
    setFilters((prev) => ({ ...prev, ...patch }))
  }, [])

  const clearFilters = useCallback(() => {
    setFilters(EMPTY_DOCUMENT_LIST_FILTERS)
  }, [])

  if (!valid || !movement) {
    return <Navigate to="/transfer/customer?movement=transfer_in" replace />
  }

  const onRowPress = (document: RfidDocumentRow) => {
    sessionStorage.setItem(BASE_DOC_STORAGE_KEY, JSON.stringify(document))
    navigate(
      `/transfer/items?movement=${movement.id}` +
        `&bpCode=${encodeURIComponent(bpCode)}` +
        `&customerName=${encodeURIComponent(customerName)}` +
        `&baseDoc=1`
    )
  }

  return (
    <PageContainer fluid className="!px-0 !py-0" stack={false}>
      <div className="rfid-web-frame">
        <RfidAppHeader
          title="Transfer base documents"
          subtitle={`${movementFlowLabel(movement)} · ${customerName}`}
          onBack={() => navigate(`/transfer/customer?movement=${movement.id}`)}
        />

        <div className="rfid-wizard-body overflow-y-auto">
          {docsQ.isLoading ? (
            <PageLoading tip="Loading base documents…" />
          ) : docsQ.isError ? (
            <PageError
              message="Failed to load base documents"
              description={(docsQ.error as Error)?.message}
            />
          ) : (
            <>
              <DocumentListToolbar
                filters={filters}
                onChange={patchFilters}
                onClear={clearFilters}
                customerOptions={customerOptions}
                plantOptions={plantOptions}
                resultCount={filtered.length}
              />

              {rows.length === 0 ? (
                <PageEmpty description="No base documents found for this customer." />
              ) : filtered.length === 0 ? (
                <PageEmpty description="No documents match your filters." />
              ) : (
                <div className="rfid-doc-list">
                  {filtered.map(({ document, raw }) => (
                    <DocumentListRow
                      key={String(document.DocumentNo ?? raw.DOCUMENT_NO)}
                      document={{
                        ...document,
                        DocumentNo: String(document.DocumentNo ?? raw.DOCUMENT_NO ?? '—'),
                        CustName: String(
                          document.CustName ??
                            document.Sender ??
                            raw.SENDER ??
                            customerName ??
                            '—'
                        ),
                      }}
                      onClick={() => onRowPress(document)}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </PageContainer>
  )
}

export { BASE_DOC_STORAGE_KEY }
